import React, { useState } from 'react';
import { Popover, Box, Typography, Chip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import unavailableIcon from 'assets/images/unavailable.png';
import minorIcon from 'assets/images/minor.png';
import overtimeIcon from 'assets/images/overtime.png';
import './EmployeeStatusIndicator.scss';

export type EmployeeStatus =
	| 'not_available'
	| 'time_off'
	| 'approaching_overtime'
	| 'in_overtime'
	| 'minor';

export interface EmployeeStatusInfo {
	type: EmployeeStatus;
	label: string;
	details?: string;
	severity: 'warning' | 'error' | 'info';
}

interface EmployeeStatusIndicatorProps {
	statuses: EmployeeStatusInfo[];
	employeeName?: string;
}

const getStatusColor = (severity: 'warning' | 'error' | 'info'): string => {
	switch (severity) {
		case 'error':
			return '#d32f2f';
		case 'warning':
			return '#ed6c02';
		case 'info':
			return '#0288d1';
		default:
			return '#ed6c02';
	}
};

const getStatusIconSrc = (type: EmployeeStatus): string => {
	switch (type) {
		case 'not_available':
		case 'time_off':
			return unavailableIcon;
		case 'approaching_overtime':
		case 'in_overtime':
			return overtimeIcon;
		case 'minor':
			return minorIcon;
		default:
			return unavailableIcon;
	}
};

export const EmployeeStatusIndicator: React.FC<EmployeeStatusIndicatorProps> = ({
	statuses,
	employeeName,
}) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	if (!statuses || statuses.length === 0) {
		return null;
	}

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);
	const id = open ? 'employee-status-popover' : undefined;
	// Group statuses by type
	const hasUnavailable = statuses.some((s) => s.type === 'not_available' || s.type === 'time_off');
	const hasMinor = statuses.some((s) => s.type === 'minor');
	const hasOvertime = statuses.some(
		(s) => s.type === 'in_overtime' || s.type === 'approaching_overtime'
	);

	return (
		<>
			<div
				className="employee-status-indicator"
				onClick={handleClick}
				style={{
					cursor: 'pointer',
					display: 'inline-flex',
					alignItems: 'center',
					marginLeft: '8px',
					gap: '4px',
				}}
			>
				{hasUnavailable && (
					<img
						src={unavailableIcon}
						alt="Unavailable"
						style={{
							width: '24px',
							height: '24px',
							objectFit: 'contain',
						}}
					/>
				)}
				{hasMinor && (
					<img
						src={minorIcon}
						alt="Minor"
						style={{
							width: '24px',
							height: '24px',
							objectFit: 'contain',
						}}
					/>
				)}
				{hasOvertime && (
					<img
						src={overtimeIcon}
						alt="Overtime"
						style={{
							width: '24px',
							height: '24px',
						}}
					/>
				)}
			</div>
			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<Box sx={{ p: 2, minWidth: 250, maxWidth: 350 }}>
					{employeeName && (
						<Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
							{employeeName} - Status Alerts
						</Typography>
					)}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
						{statuses.map((status, index) => (
							<Box
								key={index}
								sx={{
									display: 'flex',
									alignItems: 'flex-start',
									gap: 1,
									p: 1,
									borderRadius: 1,
									backgroundColor: 'rgba(0, 0, 0, 0.02)',
								}}
							>
								<img
									src={getStatusIconSrc(status.type)}
									alt={status.label}
									style={{
										width: '22px',
										height: '22px',
										objectFit: 'contain',
										marginTop: '2px',
									}}
								/>
								<Box sx={{ flex: 1 }}>
									<Chip
										label={status.label}
										size="small"
										sx={{
											backgroundColor: getStatusColor(status.severity),
											color: 'white',
											fontWeight: 500,
											fontSize: '11px',
											height: '20px',
											mb: 0.5,
										}}
									/>
									{status.details && (
										<Typography
											variant="caption"
											sx={{
												display: 'block',
												color: 'text.secondary',
												mt: 0.5,
												fontWeight: 600,
											}}
										>
											{status.details}
										</Typography>
									)}
								</Box>
							</Box>
						))}
					</Box>
				</Box>
			</Popover>
		</>
	);
};

export default EmployeeStatusIndicator;
